import { useCallback, useContext, useEffect, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import moment from 'moment'
import { rem } from 'polished'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import Caret from 'components/Common/Caret'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import {
  getElementLogicalInlineStartOffset,
  scrollToLogicalPosition,
} from 'LuxKit/Carousel/utils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getFlashCalendarLosDurations } from 'selectors/offerPage/flashOfferLosCalendar'

const TILE_WIDTH = 148

export function getCenteredScrollLeft(
  tileStart: number,
  tileWidth: number,
  viewportWidth: number,
  maxScroll: number,
): number {
  const unclamped = tileStart - ((viewportWidth - tileWidth) / 2)
  const max = Math.max(0, maxScroll)
  return Math.min(Math.max(unclamped, 0), max)
}

function impliedStayCheckOut(checkIn: string, duration: number): string {
  return moment(checkIn, ISO_DATE_FORMAT).add(duration, 'days').format(ISO_DATE_FORMAT)
}

const TileCaret = styled(Caret)`
  position: absolute;
  inset-block-end: 0;
  inset-inline-start: 50%;
  transform: translateY(100%) translateX(-50%);
  border-top-color: ${props => props.theme.palette.highlight.primary.darkBackground};
  border-width: ${rem(6)};
  pointer-events: none;

  [dir='rtl'] & {
    transform: translateY(100%) translateX(50%);
  }
`

const Tile = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(2)};
  min-height: ${rem(64)};
  padding: ${rem(10)} ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  color: ${props => props.theme.palette.neutral.default.one};
  text-align: center;

  &.selected {
    border-color: ${props => props.theme.palette.highlight.primary.darkBackground};
    background-color: ${props => props.theme.palette.highlight.primary.darkBackground};
    color: ${props => props.theme.palette.highlight.primary.darkContrast};
  }
`

const TileButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(4)};
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: center;
  cursor: pointer;

  ${mediaHoverable} {
    &:hover {
      color: ${props => props.theme.palette.brand.primary.normal};
    }
  }

  .selected & {
    color: inherit;

    ${mediaHoverable} {
      &:hover {
        color: inherit;
      }
    }
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.palette.brand.primary.normal};
    outline-offset: ${rem(2)};
  }
`

const LegendSwatch = styled.span`
  width: ${rem(12)};
  height: ${rem(12)};
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.highlight.primary.darkBackground};
`

interface Props {
  durations: Array<number>
  selectedDuration: number
  checkIn?: string
  onSelect: (duration: number) => void
}

export function FlashOfferLosCarouselView(props: Props) {
  const { durations, selectedDuration, checkIn, onSelect } = props
  const intl = useIntl()
  const carouselRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const carousel = carouselRef.current
    if (!carousel) return

    const centerSelectedTile = () => {
      const tile = carousel.querySelector<HTMLElement>(`[data-los-duration="${selectedDuration}"]`)
      if (!tile) return

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const tileStart = getElementLogicalInlineStartOffset(tile, carousel)
      const maxScroll = Math.max(0, carousel.scrollWidth - carousel.clientWidth)
      scrollToLogicalPosition(carousel, {
        left: getCenteredScrollLeft(tileStart, tile.offsetWidth, carousel.clientWidth, maxScroll),
        behavior: reduceMotion ? 'auto' : 'smooth',
      })
    }

    centerSelectedTile()
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(centerSelectedTile)
    observer.observe(carousel)
    return () => observer.disconnect()
  }, [selectedDuration])

  const onTileClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const duration = Number(event.currentTarget.dataset.duration)
    if (!durations.includes(duration)) return
    onSelect(duration)
  }, [durations, onSelect])

  const selectedStayLabel = intl.formatMessage({
    id: 'hqtgcfKE8kkYFAs8gv8Q',
    defaultMessage: 'Selected stay',
    description: 'Legend label under the Flash Offer stay-length carousel for the filled selected tile',
  })

  if (durations.length < 2) return null

  return (
    <Group direction="vertical" gap={16} fullWidth>
      <CardCarousel
        ref={carouselRef}
        pageSize="auto"
        gap={8}
        width={rem(TILE_WIDTH)}
        gutterSize={0}
        data-testid="flash-offer-los-carousel"
      >
        {durations.map(duration => {
          const selected = duration === selectedDuration
          const nightsLabel = intl.formatMessage({
            id: 'fFvCQ5ZkESx9NB3FFhOW',
            defaultMessage: '{count, plural, one {# night} other {# nights}}',
            description: 'Stay length shown on a Flash Offer calendar length-of-stay tile',
          }, { count: duration })
          const impliedCheckOut = checkIn ? impliedStayCheckOut(checkIn, duration) : undefined
          const sameYear = !impliedCheckOut || checkIn?.slice(0, 4) === impliedCheckOut.slice(0, 4)
          const dateRange = checkIn && impliedCheckOut
            ? intl.formatMessage({
                id: 'Ig3uJBp7-kqFKiZnm0QQ',
                defaultMessage: '{checkIn} – {checkOut}',
                description: 'Check-in and check-out dates on a Flash Offer stay-length tile when a check-in is selected',
              }, {
                checkIn: intlDateFormatter(checkIn, INTL_DAY_MONTH_NAME_SHORT, intl.locale),
                checkOut: intlDateFormatter(
                  impliedCheckOut,
                  sameYear ? INTL_DAY_MONTH_NAME_SHORT : INTL_DMY_CASUAL_SHORT_FORMAT,
                  intl.locale,
                ),
              })
            : undefined

          return (
            <Tile
              key={duration}
              data-los-duration={duration}
              className={clsx({ selected })}
              data-testid={`flash-offer-los-${duration}`}
            >
              <TileButton
                type="button"
                data-duration={duration}
                aria-pressed={selected}
                onClick={onTileClick}
              >
                <BodyText variant="medium" weight="bold" align="center">{nightsLabel}</BodyText>
                {dateRange && <BodyText variant="medium" weight="bold" align="center">{dateRange}</BodyText>}
              </TileButton>
              {selected && <TileCaret />}
            </Tile>
          )
        })}
      </CardCarousel>
      <Group direction="horizontal" gap={8} verticalAlign="center">
        <LegendSwatch />
        <Caption variant="medium" colour="neutral-two">{selectedStayLabel}</Caption>
      </Group>
    </Group>
  )
}

export function FlashOfferLosCarousel(props: {
  durations: Array<number>
  selectedDuration: number
  checkIn?: string
}) {
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const { durations, selectedDuration, checkIn } = props

  const onSelect = useCallback((duration: number) => {
    if (duration === selectedDuration) return
    dispatch({ type: OfferPageActions.setDuration, duration })
    trackEvent('DURATION_PRESSED')
  }, [dispatch, selectedDuration, trackEvent])

  return (
    <FlashOfferLosCarouselView
      durations={durations}
      selectedDuration={selectedDuration}
      checkIn={checkIn}
      onSelect={onSelect}
    />
  )
}

export default withOfferPageAndReduxState((state) => ({
  durations: getFlashCalendarLosDurations(state),
  selectedDuration: state.duration,
  checkIn: state.checkInDate,
}), FlashOfferLosCarousel)
